import { FC, useCallback, useState } from 'react';
import { Box, Skeleton, SxProps, Theme, Typography } from '@mui/material';
import { Button, clsx, Colors, Showable, TextField } from '@sweatpals/react-ui-kit';
import { IsNotEmpty, IsPhoneNumber, IsEmail, MinLength, IsString } from 'class-validator';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { GetUserOrUpsertTempUser } from '@sweatpals/shared';
import { useForm } from 'react-hook-form';
import { CorePhoneInput } from '@/modules/_core/components/_ui/core-phone-input';

export class WaitlistUserData implements GetUserOrUpsertTempUser {
	@IsString()
	@IsNotEmpty({ message: 'Full name is required' })
	@MinLength(3, { message: 'Full name should be at least 3 chars' })
	public fullName: string;

	@IsNotEmpty({ message: 'Phone number is required' })
	@IsPhoneNumber(undefined, { message: 'Invalid phone number' })
	@MinLength(3, { message: 'Phone number should be at least 3 chars' })
	public phone: string;

	@IsNotEmpty({ message: 'Email is required' })
	@IsEmail({}, { message: 'Invalid email' })
	public email: string;
}

const resolver = classValidatorResolver(WaitlistUserData);

export interface JoinWaitlistProps {
	isAlreadyJoined?: boolean;
	showSkeleton?: boolean;
	sx?: SxProps<Theme>;
	disabled?: boolean;
	loading?: boolean;
	Header?: FC;
	skipSuccessStep?: boolean;
	onAlreadyJoinedConfirmButtonClick?(): void;
	onSuccess(data: WaitlistUserData): void | Promise<void>;
}

export const JoinWaitlist: FC<JoinWaitlistProps> = props => {
	const {
		showSkeleton,
		isAlreadyJoined,
		sx,
		disabled,
		loading,
		skipSuccessStep,
		Header,
		onSuccess,
		onAlreadyJoinedConfirmButtonClick
	} = props;

	const [step, setStep] = useState(0);

	const form = useForm({
		defaultValues: {
			fullName: '',
			phone: '',
			email: '',
		},
		resolver,
	});

	const values = form.watch();

	const handleSubmitForm = useCallback(
		async (data: WaitlistUserData) => {
			if (form.formState.isValid) {
				await onSuccess(data);

				if (!skipSuccessStep) {
					setStep(1);
				}
			}
		},
		[form.formState.isValid, onSuccess, skipSuccessStep]
	);

	return (
		<Box
			sx={clsx({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: '18px',
				padding: '24px',
				textAlign: 'center',
				backgroundColor: Colors['191919'],
				position: 'relative',
			}, sx)}
		>
			<Showable show={showSkeleton}>
				<Box display="flex" justifyContent="center">
					<Skeleton
						sx={{
							height: '20px',
							width: '98px',
							backgroundColor: Colors.white_a30,
							borderRadius: '20px',
						}}
						variant="rectangular"
					/>
				</Box>
				<Box display="flex" justifyContent="center" gap="18px">
					<Skeleton
						sx={{
							height: '8px',
							width: '67px',
							backgroundColor: Colors.white_a20,
							borderRadius: '8px',
						}}
						variant="rectangular"
					/>
					<Skeleton
						sx={{
							height: '8px',
							width: '179px',
							backgroundColor: Colors.white_a20,
							borderRadius: '8px',
						}}
						variant="rectangular"
					/>
				</Box>
				<Skeleton
					sx={{
						height: '46px',
						width: '100%',
						backgroundColor: Colors.white_a5,
						borderRadius: '12px',
					}}
					variant="rectangular"
				/>
				<Box sx={{ display: 'flex', gap: '12px', width: '100%', opacity: 0.65 }}>
					<Skeleton
						sx={{
							height: '46px',
							width: '95px',
							backgroundColor: Colors.white_a5,
							borderRadius: '12px',
						}}
						variant="rectangular"
					/>
					<Skeleton
						sx={{
							height: '46px',
							flex: 1,
							backgroundColor: Colors.white_a5,
							borderRadius: '12px',
						}}
						variant="rectangular"
					/>
				</Box>
				<Box sx={{ width: '100%', opacity: 0.3 }}>
					<Skeleton
						sx={{
							height: '46px',
							width: '100%',
							backgroundColor: Colors.white_a5,
							borderRadius: '12px',
						}}
						variant="rectangular"
					/>
				</Box>
				<Box sx={{ display: 'flex', justifyContent: 'center', opacity: 0.3 }}>
					<Skeleton
						sx={{
							height: '46px',
							width: '108px',
							backgroundColor: Colors.white_a5,
							borderRadius: '46px',
						}}
						variant="rectangular"
					/>
				</Box>
			</Showable>
			<Showable show={!showSkeleton}>
				{Header && <Header />}

				<Showable show={step === 0}>
					{() => isAlreadyJoined ? (
						<>
							<Typography
								sx={{
									fontFamily: 'Montserrat',
									color: Colors.white,
									fontSize: '24px',
									fontWeight: '600',
									lineHeight: '18px',
								}}
							>
								You are already on the waitlist
							</Typography>
							<Typography
								sx={{
									fontFamily: 'Montserrat',
									color: Colors.white,
									fontSize: '15px',
									fontWeight: '400',
									lineHeight: '21px',
								}}
							>
								We will notify you once spot is available
							</Typography>

							<Showable show={!!onAlreadyJoinedConfirmButtonClick}>
								<Box display="flex" justifyContent="center">
									<Button strictColor onClick={onAlreadyJoinedConfirmButtonClick}>
										OK
									</Button>
								</Box>
							</Showable>
						</>
					) : (
						<>
							<Typography
								sx={{
									fontFamily: 'Montserrat',
									color: Colors.white,
									fontSize: '24px',
									fontWeight: '600',
									lineHeight: '18px',
								}}
							>
								Join waitlist
							</Typography>
							<Typography
								sx={{
									fontFamily: 'Montserrat',
									color: Colors.white,
									fontSize: '15px',
									fontWeight: '400',
									lineHeight: '21px',
								}}
							>
								Enter your email and we will notify you once spot is available.
							</Typography>
							<Box
								component='form'
								onSubmit={form.handleSubmit(handleSubmitForm)}
								sx={{
									width: '100%',
									display: 'flex',
									flexDirection: 'column',
									gap: '18px',
								}}
							>
								<TextField
									{...form.register('fullName')}
									value={values.fullName}
									onChange={e => form.setValue('fullName', e.target.value)}
									fullWidth
									placeholder='Full Name'
									error={!!form.formState.errors.fullName}
									disabled={disabled}
									helperText={form.formState.errors.fullName?.message}
									sx={{
										'&.MuiInputBase-root': {
											backgroundColor: `${Colors.white_a5} !important`,
											borderRadius: '8px',
										},
									}}
								/>
								<CorePhoneInput
									controlId='phone'
									id='phone-input'
									fullWidth
									placeholder='Phone Number'
									name='phoneNumber'
									disabled={disabled}
									defaultCountry='US'
									forceCallingCode
									MenuProps={{ disableAutoFocusItem: true }}
									value={values.phone}
									onChange={phoneNumber => {
										form.setValue('phone', phoneNumber, {
											shouldValidate: true,
											shouldDirty: true,
										});
									}}
									error={!!form.formState.errors?.phone}
									helperText={form.formState.errors?.phone?.message}
									sx={{
										'& .MuiInputBase-root': {
											backgroundColor: `${Colors.white_a5} !important`,
											borderRadius: '8px',
										},
									}}
								/>
								<TextField
									{...form.register('email')}
									value={values.email}
									onChange={e => form.setValue('email', e.target.value)}
									fullWidth
									placeholder='Email'
									error={!!form.formState.errors.email}
									disabled={disabled}
									helperText={form.formState.errors.email?.message}
									sx={{
										'&.MuiInputBase-root': {
											backgroundColor: `${Colors.white_a5} !important`,
											borderRadius: '8px',
										},
									}}
								/>
								<Box display="flex" justifyContent="center">
									<Button strictColor type='submit' disabled={disabled} loading={loading}>
										Confirm
									</Button>
								</Box>
							</Box>
						</>
					)}
				</Showable>

				<Showable show={step === 1}>
					{() => (
						<>
							<Typography
								sx={{
									fontFamily: 'Montserrat',
									color: Colors.white,
									fontSize: '24px',
									fontWeight: '600',
									lineHeight: '18px',
								}}
							>
								Thank you!
							</Typography>
							<Typography
								sx={{
									fontFamily: 'Montserrat',
									color: Colors.white,
									fontSize: '15px',
									fontWeight: '400',
									lineHeight: '21px',
								}}
							>
								We will notify you once spot is available
							</Typography>
						</>
					)}
				</Showable>
			</Showable>
		</Box>
	);
};
